import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { sidebarSelector, toggleSideBar } from "../../redux/reducers/sidebar";
import useIsMobile from "../../hooks/isMobile";
import { selectUser } from "../../components/auth/auth.selector";
import { BodyBuilder } from "../../exports/Images";
import {
  ThemeIcon,
  BellIcon,
  ChatIcon,
  ExpandIcon,
  ExitFullscreenIcon,
  ChevronDownIconSmall,
} from "../icons/TopBarIcons";
import CurrencyDropdown from "../dropdown/CurrencyDropdown";
import ThemeDropdown from "../dropdown/ThemeDropdown";
import ProfileDropdown from "../dropdown/ProfileDropdown";
import PermanentSearchBar from "../../components/PermanentSearchBar";
import { useCurrency } from "../../context-v2/CurrencyContext";
import "../../styles-v2/topbar/v2-topbar.css";
import "../../styles-v2/topbar/v2-topbar-search.css";

const TopBarV2 = () => {
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(sidebarSelector);
  const userInfoData = useSelector(selectUser);
  const isMobile = useIsMobile();
  const { currency, setCurrency, currencies } = useCurrency();
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleThemeToggle = () => {
    setIsThemeDropdownOpen(!isThemeDropdownOpen);
  };

  const handleNotificationClick = () => {
    // Implement notification functionality
    console.log("Open notifications");
  };

  const handleChatClick = () => {
    // Implement chat functionality
    console.log("Open chat");
  };

  const handleExpandClick = () => {
    // Toggle fullscreen
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  const handleProfileClick = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const topbarClass = `v2-topbar ${
    isSidebarOpen
      ? "v2-topbar--sidebar-expanded"
      : "v2-topbar--sidebar-collapsed"
  }`;

  return (
    <header className={topbarClass}>
      {/* Mobile Hamburger Menu */}
      {isMobile && (
        <button
          className="v2-topbar__hamburger"
          onClick={() => dispatch(toggleSideBar(!isSidebarOpen))}
          title="Toggle menu"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      )}

      {/* Search Section */}
      <div className="v2-topbar__search">
        <PermanentSearchBar />
      </div>

      {/* Actions Section */}
      <div className="v2-topbar__actions">
        {/* Theme Toggle */}
        <div className="v2-topbar__theme">
          <button
            className="v2-topbar__action-btn"
            onClick={handleThemeToggle}
            title="Toggle theme"
          >
            <ThemeIcon size={20} />
          </button>
          <ThemeDropdown
            isOpen={isThemeDropdownOpen}
            onClose={() => setIsThemeDropdownOpen(false)}
          />
        </div>

        {/* Notifications */}
        {/* <button
          className="v2-topbar__action-btn"
          onClick={handleNotificationClick}
          title="Notifications"
        >
          <BellIcon size={20} />
          <span className="v2-topbar__badge">3</span>
        </button> */}

        {/* Currency */}
        <div className="v2-topbar__currency">
          <button
            className="v2-topbar__currency-btn"
            title="Select currency"
            onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
          >
            <span className="v2-topbar__currency-flag">
              {currency.flag}
            </span>
            <span className="v2-topbar__currency-text">
              {currency.code}
            </span>
            <ChevronDownIconSmall size={14} />
          </button>
          <CurrencyDropdown
            isOpen={isCurrencyDropdownOpen}
            onClose={() => setIsCurrencyDropdownOpen(false)}
            onSelect={setCurrency}
            selectedCurrency={currency}
            currencies={currencies}
          />
        </div>

        {/* Chat */}
        {/* <button
          className="v2-topbar__action-btn"
          onClick={handleChatClick}
          title="Chat"
        >
          <ChatIcon size={20} />
          <span className="v2-topbar__indicator"></span>
        </button> */}

        {/* Expand */}
        <button
          className="v2-topbar__action-btn"
          onClick={handleExpandClick}
          title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <ExitFullscreenIcon size={20} />
          ) : (
            <ExpandIcon size={20} />
          )}
        </button>

        <div className="v2-topbar__divider"></div>

        {/* Profile */}
        <div className="v2-topbar__profile-wrapper">
          <button
            className="v2-topbar__profile"
            onClick={handleProfileClick}
            title="Profile"
          >
            <img
              src={userInfoData?.profile || BodyBuilder}
              alt="Profile"
              className="v2-topbar__profile-img"
            />
          </button>
          <ProfileDropdown
            isOpen={isProfileDropdownOpen}
            onClose={() => setIsProfileDropdownOpen(false)}
          />
        </div>
      </div>
    </header>
  );
};

export default TopBarV2;
